import { gql } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconServices from '@apollo/icons/default/IconServices.svg';
import { Avatar } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { ImplementingService } from '../explorerPage/helpers/federationHelpers';
import { GraphRef } from '../hooks/useGraphRef';

import { GraphSelect, GraphSelectFragment } from './GraphSelect';
import { MultiOrgSelect, MultiOrgSelectFragment } from './MultiOrgSelect';
import { SubgraphSelect } from './SubgraphSelect';
import { VariantSelect } from './VariantSelect';

export const SandboxGraphRefSelectorsUserFragment = gql`
  # Note that this fragment fetches all variants on all graphs
  # for each account on this user. Avoid adding expensive fields
  # (like the schema document) to this query
  fragment SandboxGraphRefSelectorsUserFragment on User {
    id
    memberships {
      user {
        id
      }
      account {
        id
        name
        avatarUrl
        services {
          id
          title
          description
          variants {
            id
            permissions {
              canShareCollectionInVariant
            }
          }
          ...GraphSelectFragment
        }
        ...MultiOrgSelectFragment
      }
    }
  }
  ${GraphSelectFragment}
  ${MultiOrgSelectFragment}
`;

export type SandboxSelectorState = {
  orgId: string;
  graphId: string;
  variantId: string;
  subgraphName?: string;
};

export const SandboxGraphRefSelectors = ({
  setSelectedGraphRef,
  setSelectedSubgraphName: passedSetSelectedSubgraphName,
  user,
  disabledVariantItemChecker,
  disabledGraphItemChecker: passedDisabledGraphItemChecker,
  disabledOrgItemChecker: passedDisabledOrgItemChecker,
  introspectedEndpointIsSubgraph,
  subgraphs,
  initialSelectorState: passedInitialSelectorState,
  className,
}: {
  setSelectedGraphRef: React.Dispatch<
    React.SetStateAction<GraphRef | undefined>
  >;
  setSelectedSubgraphName?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  user: GraphQLTypes.SandboxGraphRefSelectorsUserFragment;
  introspectedEndpointIsSubgraph: boolean;
  subgraphs: ImplementingService[] | null;
  disabledVariantItemChecker?: (
    variant: GraphQLTypes.VariantSelectFragment,
  ) => { isDisabled: boolean; disabledExplanation: string };
  disabledGraphItemChecker?: (graph: GraphQLTypes.GraphSelectFragment) => {
    isDisabled: boolean;
    disabledExplanation: string;
  };
  disabledOrgItemChecker?: (org: GraphQLTypes.MultiOrgSelectFragment) => {
    isDisabled: boolean;
    disabledExplanation: string;
  };
  initialSelectorState?: SandboxSelectorState | undefined;
  className?: string;
}) => {
  const [currentAccountId] = useCurrentAccountId();
  const orgs = useMemo(
    () => user?.memberships?.map((membership) => membership.account),
    [user],
  );

  const disabledGraphItemChecker = useCallback(
    (graph: GraphQLTypes.GraphSelectFragment) => {
      if (
        graph.variants.every((variant) => {
          return disabledVariantItemChecker?.(variant).isDisabled;
        })
      ) {
        return {
          isDisabled: true,
          disabledExplanation:
            graph.variants.length === 0
              ? "This graph doesn't have any variants."
              : "This graph doesn't have any valid variants to compare to.",
        };
      }

      return passedDisabledGraphItemChecker?.(graph);
    },
    [passedDisabledGraphItemChecker, disabledVariantItemChecker],
  );
  const disabledOrgItemChecker = useCallback(
    (org: GraphQLTypes.MultiOrgSelectFragment) => {
      if (
        org.services.every((service) => {
          return disabledGraphItemChecker?.(service)?.isDisabled;
        })
      ) {
        return {
          isDisabled: true,
          disabledExplanation:
            org.services.length === 0
              ? "This org doesn't have any graphs."
              : "This org doesn't have any valid graphs to compare to.",
        };
      }

      return passedDisabledOrgItemChecker?.(org);
    },
    [passedDisabledOrgItemChecker, disabledGraphItemChecker],
  );

  const initialSelectorState = useMemo(() => {
    if (!passedInitialSelectorState) return;
    const initialOrg = orgs.find(
      (org) => org.id === passedInitialSelectorState.orgId,
    );

    if (!initialOrg) return;
    const orgIsDisabled = disabledOrgItemChecker?.(initialOrg)?.isDisabled;

    const initialGraph = initialOrg?.services.find(
      (graph) => graph.id === passedInitialSelectorState.graphId,
    );

    if (!initialGraph) return;
    const graphIsDisabled =
      disabledGraphItemChecker?.(initialGraph)?.isDisabled;

    const initialVariant = initialGraph?.variants.find(
      (variant) => variant.id === passedInitialSelectorState.variantId,
    );

    if (!initialVariant) return;
    const variantIsDisabled =
      disabledVariantItemChecker?.(initialVariant).isDisabled;

    return {
      orgId: !orgIsDisabled ? passedInitialSelectorState.orgId : undefined,
      graphId:
        !orgIsDisabled && !graphIsDisabled
          ? passedInitialSelectorState.graphId
          : undefined,
      variantId:
        !orgIsDisabled && !graphIsDisabled && !variantIsDisabled
          ? passedInitialSelectorState.variantId
          : undefined,
      subgraphName: passedInitialSelectorState.subgraphName,
    };
  }, [
    disabledGraphItemChecker,
    disabledOrgItemChecker,
    disabledVariantItemChecker,
    orgs,
    passedInitialSelectorState,
  ]);

  /**
   * Orgs functionality
   */
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(() => {
    if (initialSelectorState?.orgId) {
      return initialSelectorState.orgId;
    }
    if (
      currentAccountId &&
      orgs?.some(
        (org) =>
          org.id === currentAccountId &&
          !disabledOrgItemChecker?.(org)?.isDisabled,
      )
    ) {
      return currentAccountId;
    }
    if (isMinLengthArray(1, orgs)) {
      return orgs.find((org) => !disabledOrgItemChecker?.(org)?.isDisabled)?.id;
    }
  });
  const selectedOrg = useMemo(
    () => orgs?.find((org) => org.id === selectedOrgId),
    [orgs, selectedOrgId],
  );

  /**
   * Graphs functionality
   */
  const [selectedGraphId, setSelectedGraphId] = useState<string | null>(
    initialSelectorState?.graphId ?? null,
  );
  const selectedGraph = useMemo(
    () => selectedOrg?.services.find((graph) => graph.id === selectedGraphId),
    [selectedOrg, selectedGraphId],
  );

  /**
   * Variants functionality
   */
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    initialSelectorState?.variantId ?? null,
  );
  const selectedVariant = useMemo(() => {
    return selectedGraph?.variants.find(
      (variant) => variant.id === selectedVariantId,
    );
  }, [selectedGraph, selectedVariantId]);

  /**
   * Subgraph functionality
   */
  const [selectedSubgraphName, setSelectedSubgraphName] = useState<
    string | null
  >(initialSelectorState?.subgraphName ?? null);

  const [_, setSelectorState] = useLocalStorage(
    'sandboxCheckDiffSelectorState',
  );

  useEffect(() => {
    if (selectedOrgId && selectedGraphId && selectedVariantId) {
      setSelectorState?.({
        orgId: selectedOrgId,
        graphId: selectedGraphId,
        variantId: selectedVariantId,
        subgraphName: selectedSubgraphName ?? undefined,
      });
    }
  }, [
    selectedGraphId,
    selectedOrgId,
    selectedVariantId,
    selectedSubgraphName,
    setSelectorState,
  ]);

  // When user goes from a subgraph endpoint to a non subgraph endpoint,
  // remove subgraph from selected graph ref
  useEffect(() => {
    if (!introspectedEndpointIsSubgraph) {
      setSelectedSubgraphName(null);
    }
  }, [introspectedEndpointIsSubgraph]);

  // The below 2 effects keep the parent state for graph ref & subgraph in line
  // with the local state of the selectors. We have seperate state for the selectors
  // compared to the parent so that we can populate the selectedGraphRef only when a variant
  // & a graph Id have been selected. We could swap out the local subgraph state for the parent
  // state only here, but in the spirit of keeping all selector state in the same place,
  // we have a local subgraph state for SandboxGraphRefSelectors that we keep in line
  // with parent subgraph state in this useEffect.
  useEffect(() => {
    if (selectedVariant && selectedGraph) {
      setSelectedGraphRef({
        graphVariant: selectedVariant.name,
        graphId: selectedGraph.id,
      });
    } else {
      setSelectedGraphRef(undefined);
    }
  }, [selectedGraph, selectedVariant, setSelectedGraphRef]);

  useEffect(() => {
    if (selectedSubgraphName) {
      passedSetSelectedSubgraphName?.(selectedSubgraphName);
    } else {
      passedSetSelectedSubgraphName?.(undefined);
    }
  }, [passedSetSelectedSubgraphName, selectedSubgraphName]);

  const handleSetSelectedGraphId = useCallback(
    (graphId: string | null) => {
      setSelectedGraphId(graphId);
      let variantIdToSelect = null;
      const newlySelectedGraph = selectedOrg?.services.find(
        (graph) => graph.id === graphId,
      );
      /**
       * Set the initial selected variant everytime the graph changes.
       * If there is only one non disabled variant in this graph,
       * autofill the variant select, otherwise, reset variant id when graph id changes
       */
      const chooseableVariants = newlySelectedGraph?.variants.filter(
        (variant) =>
          disabledVariantItemChecker
            ? !disabledVariantItemChecker(variant).isDisabled
            : true,
      );
      if (chooseableVariants?.length === 1 && chooseableVariants[0]) {
        variantIdToSelect = chooseableVariants[0].id;
      }
      setSelectedVariantId(variantIdToSelect);
    },
    [disabledVariantItemChecker, selectedOrg],
  );

  const handleSetSelectedVariantId = useCallback((variantId: string | null) => {
    setSelectedVariantId(variantId);
    setSelectedSubgraphName(null);
  }, []);

  return (
    <>
      <div
        className={classnames(
          className,
          'mx-auto flex w-auto items-center p-1 text-secondary',
        )}
      >
        {orgs.length > 1 ? (
          <MultiOrgSelect
            selectedOrgId={selectedOrg?.id || null}
            setSelectedOrgId={setSelectedOrgId}
            orgs={orgs}
            triggerContent={
              <div className="flex items-center">
                {selectedOrg ? (
                  <Avatar
                    name={selectedOrg.name}
                    src={selectedOrg.avatarUrl ?? undefined}
                    size="xs"
                    variant="org"
                  />
                ) : (
                  <div className="size-5 rounded-md bg-silver-darker" />
                )}
              </div>
            }
            disabledItemChecker={disabledOrgItemChecker}
          />
        ) : (
          <Avatar
            className="mx-2"
            name={selectedOrg?.name || 'none'}
            src={selectedOrg?.avatarUrl || undefined}
            size="xs"
            variant="org"
          />
        )}
        <GraphSelect
          triggerText={selectedGraph?.title || 'select a graph'}
          selectedGraphId={selectedGraph?.id ?? null}
          setSelectedGraphId={handleSetSelectedGraphId}
          graphs={selectedOrg?.services}
          disableTrigger={!selectedOrg}
          disabledItemChecker={disabledGraphItemChecker}
        />
        <div className="mb-1 font-semibold">@</div>
        <VariantSelect
          triggerText={selectedVariant?.name || 'select a variant'}
          selectedVariantId={selectedVariant?.id ?? null}
          setSelectedVariantId={handleSetSelectedVariantId}
          variants={selectedGraph?.variants}
          disableTrigger={!selectedGraph}
          disabledItemChecker={disabledVariantItemChecker}
        />
        {introspectedEndpointIsSubgraph && (
          <>
            <IconServices className="flex items-center" />
            <SubgraphSelect
              triggerText={selectedSubgraphName || 'select a subgraph'}
              selectedSubgraphName={selectedSubgraphName ?? null}
              setSelectedSubgraphName={setSelectedSubgraphName}
              disableTrigger={!selectedVariant || !subgraphs}
              subgraphs={subgraphs ?? undefined}
            />
          </>
        )}
      </div>
    </>
  );
};

export const DisabledSandboxGraphRefSelectors = ({
  className,
}: {
  className?: string;
}) => {
  return (
    <div
      className={classnames(
        className,
        'mx-auto flex w-auto items-center p-1 text-secondary opacity-60',
      )}
    >
      <div className="mx-2 size-5 rounded bg-silver-darker" />
      <IconChevronDown className="ml-1 text-disabled" />
      <GraphSelect
        triggerText="select a graph"
        selectedGraphId={null}
        setSelectedGraphId={() => null}
        graphs={[]}
        disableTrigger={true}
      />
      <div className="flex items-center font-semibold text-disabled">@</div>
      <VariantSelect
        triggerText="select a variant"
        selectedVariantId={null}
        setSelectedVariantId={() => null}
        variants={[]}
        disableTrigger={true}
      />
    </div>
  );
};
